import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys, type ConversationListFilters, type UnifiedListFilters } from '@/api/queryKeys';
import type { ConversationSortOrder, ConversationSummary, MessageSortOrder } from '@/api/types';
import {
  compareConversations,
  type ConversationListPage,
  type ConversationPageCache,
} from '@/cache/conversationPatches';
import { signalReaches, subscribeConversationSignals } from '@/realtime/conversationSignals';
import { toConversationRow, type ConversationRow } from './conversationRows';
import { scheduleConversationSync, type ConversationSyncTarget } from './conversationSync';
import {
  emptyLanes,
  laneFailures,
  lanesHaveMore,
  lanesToRefill,
  mergeLanesWith,
  type UnifiedLane,
  type UnifiedLaneProblem,
  type UnifiedLanes,
} from './unifiedList';
import { describeLaneFailure, type UnifiedInbox } from './useUnifiedMessages';

/**
 * The conversation list (ADR 0077, plan/16 §8): a folder's, or the merged
 * one's, as one cursor-paged query — and its live path.
 *
 * Kept out of `MailView` so the flat list there is exactly the code it was:
 * with conversation view off, this hook's queries are disabled observers and
 * it sends nothing.
 */

const PAGE_SIZE = 50;

/**
 * The conversation order a message order stands for, or `undefined` when it
 * has none (sender, subject, size: those show single messages). Arrival and
 * date both mean "newest first" here: a conversation is placed by its newest
 * message's date, the column the list shows.
 */
export function conversationSortFor(sort: MessageSortOrder): ConversationSortOrder | undefined {
  if (sort === 'date-desc' || sort === 'arrival-desc') return 'date-desc';
  if (sort === 'date-asc' || sort === 'arrival-asc') return 'date-asc';
  return undefined;
}

/** A merged page: the lane merge's cursor, over conversations. */
export interface UnifiedConversationPage extends ConversationListPage {
  lanes: UnifiedLanes<ConversationSummary>;
  unavailable: UnifiedLaneProblem[];
}

/** How many fetch/merge rounds a merged page may take (see `fetchUnifiedPage`). */
const MAX_ROUNDS = 6;

/**
 * One merged page of conversations: the lanes refilled that ran dry, merged
 * by date. Conversations are per account, so nothing is merged ACROSS
 * accounts — the rows are interleaved, never combined. Each lane's `state`
 * token is kept from the first page that fetched it, for its `sync`.
 */
export async function fetchUnifiedConversationPage(options: {
  api: MailApi;
  inboxes: readonly UnifiedInbox[];
  lanes: UnifiedLanes<ConversationSummary>;
  sort: ConversationSortOrder;
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
  limit?: number;
  signal?: AbortSignal;
}): Promise<UnifiedConversationPage> {
  const { api, inboxes, sort, signal } = options;
  const compare = compareConversations(sort);
  const folderOf = new Map(inboxes.map((inbox) => [inbox.accountId, inbox.folderId]));
  let lanes = options.lanes;
  let items: ConversationSummary[] = [];
  const states: Record<string, string> = {};

  for (let round = 0; round < MAX_ROUNDS && items.length === 0; round += 1) {
    const refill = lanesToRefill(lanes).filter((accountId) => folderOf.has(accountId));
    if (refill.length > 0) {
      const answers = await Promise.allSettled(
        refill.map((accountId) =>
          api.messages.conversations(
            {
              accountId,
              folderId: folderOf.get(accountId)!,
              sort,
              limit: options.limit ?? PAGE_SIZE,
              ...(lanes[accountId]?.cursor ? { cursor: lanes[accountId]!.cursor } : {}),
              ...(options.unreadOnly ? { unreadOnly: true } : {}),
              ...(options.flaggedOnly ? { flaggedOnly: true } : {}),
            },
            signal,
          ),
        ),
      );
      const next: Record<string, UnifiedLane<ConversationSummary>> = { ...lanes };
      refill.forEach((accountId, index) => {
        const answer = answers[index]!;
        const lane = lanes[accountId]!;
        if (answer.status === 'rejected') {
          next[accountId] = { ...lane, failure: describeLaneFailure(answer.reason) };
          return;
        }
        const page = answer.value;
        // Page one of a lane is where its token comes from (see
        // `conversationSyncState` for why a later page's is not used).
        if (lane.cursor === undefined) states[accountId] = page.state;
        const { cursor: _consumed, ...rest } = lane;
        next[accountId] = {
          ...rest,
          buffer: [...lane.buffer, ...page.items],
          ...(page.nextCursor ? { cursor: page.nextCursor } : {}),
          exhausted: page.nextCursor === undefined,
        };
      });
      lanes = next;
    } else if (!lanesHaveMore(lanes)) {
      break;
    }
    const merged = mergeLanesWith(lanes, compare);
    items = merged.items;
    lanes = merged.rest;
    if (items.length === 0 && lanesToRefill(lanes).length === 0) break;
  }

  return {
    items,
    lanes,
    unavailable: laneFailures(lanes),
    sort,
    sortsAvailable: ['date-desc', 'date-asc'],
    ...(Object.keys(states).length > 0 ? { states } : {}),
  };
}

/**
 * The rows, flattened across pages. A conversation can appear twice when its
 * head was expunged and it resurfaced lower down (§7): the LATER copy is the
 * current one and takes the place, the earlier copy is dropped.
 */
export function flattenConversations(
  pages: readonly { items: readonly ConversationSummary[] }[] | undefined,
): ConversationRow[] {
  const all = (pages ?? []).flatMap((page) => page.items);
  const last = new Map<string, number>();
  all.forEach((row, index) => last.set(`${row.accountId}\u0000${row.id}`, index));
  const out: ConversationRow[] = [];
  all.forEach((row, index) => {
    if (last.get(`${row.accountId}\u0000${row.id}`) === index) out.push(toConversationRow(row));
  });
  return out;
}

export interface UseConversationListOptions {
  /** Conversation mode is on for this list. Off, nothing here fetches. */
  enabled: boolean;
  unified: boolean;
  accountId: string | undefined;
  folderId: string | undefined;
  /** The merged list's lanes. */
  inboxes: readonly UnifiedInbox[];
  sort: ConversationSortOrder;
  unreadOnly: boolean;
  flaggedOnly: boolean;
}

export function useConversationList(options: UseConversationListOptions) {
  const api = useApi();
  const client = useQueryClient();
  const { enabled, unified, accountId, folderId, inboxes, sort, unreadOnly, flaggedOnly } = options;

  const filters: ConversationListFilters = useMemo(
    () => ({
      folderId: folderId ?? '',
      sort,
      ...(unreadOnly ? { unreadOnly: true } : {}),
      ...(flaggedOnly ? { flaggedOnly: true } : {}),
    }),
    [flaggedOnly, folderId, sort, unreadOnly],
  );
  const folderKey = useMemo(
    () => queryKeys.account(accountId ?? '').conversations(filters),
    [accountId, filters],
  );

  const laneAccounts = useMemo(() => inboxes.map((inbox) => inbox.accountId).sort(), [inboxes]);
  const unifiedFilters: UnifiedListFilters = useMemo(
    () => ({
      accounts: laneAccounts.join(','),
      sort,
      ...(unreadOnly ? { unreadOnly: true } : {}),
      ...(flaggedOnly ? { flaggedOnly: true } : {}),
    }),
    [flaggedOnly, laneAccounts, sort, unreadOnly],
  );
  const unifiedKey = useMemo(() => queryKeys.unifiedConversations(unifiedFilters), [unifiedFilters]);

  const folderQuery = useInfiniteQuery({
    queryKey: folderKey,
    queryFn: ({ pageParam, signal }) =>
      api.messages.conversations(
        {
          accountId: accountId ?? '',
          ...filters,
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: PAGE_SIZE,
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && !unified && accountId !== undefined && folderId !== undefined,
    staleTime: STALE_TIMES.list,
  });

  const unifiedQuery = useInfiniteQuery({
    queryKey: unifiedKey,
    queryFn: ({ pageParam, signal }) =>
      fetchUnifiedConversationPage({
        api,
        inboxes,
        lanes: pageParam ?? emptyLanes<ConversationSummary>(laneAccounts),
        sort,
        unreadOnly,
        flaggedOnly,
        signal,
      }),
    initialPageParam: undefined as UnifiedLanes<ConversationSummary> | undefined,
    getNextPageParam: (lastPage: UnifiedConversationPage) =>
      lanesHaveMore(lastPage.lanes) ? lastPage.lanes : undefined,
    enabled: enabled && unified && inboxes.length > 0,
    staleTime: STALE_TIMES.list,
  });

  const query = unified ? unifiedQuery : folderQuery;
  const queryKey = unified ? unifiedKey : folderKey;
  const pages = (query.data as ConversationPageCache | undefined)?.pages;
  /*
   * The same array while the rows are the same objects: a sync that only
   * moved the state token replaces the first page's object, not its rows, and
   * the list must not re-render every row for it. (React's "information from
   * previous renders" pattern: the state is adjusted during render.)
   */
  const [flat, setFlat] = useState(() => ({ pages, rows: flattenConversations(pages) }));
  let rows = flat.rows;
  if (flat.pages !== pages) {
    const next = flattenConversations(pages);
    const same = next.length === rows.length && next.every((row, index) => row === rows[index]);
    if (!same) rows = next;
    setFlat({ pages, rows });
  }

  /* ------------------------------------------------------- the live path */

  const target: ConversationSyncTarget | undefined = useMemo(() => {
    if (!enabled) return undefined;
    const lanes = unified
      ? inboxes.map((inbox) => ({ accountId: inbox.accountId, folderId: inbox.folderId }))
      : accountId !== undefined && folderId !== undefined
        ? [{ accountId, folderId }]
        : [];
    if (lanes.length === 0) return undefined;
    return {
      queryKey,
      lanes,
      unified,
      sort,
      ...(unreadOnly ? { unreadOnly: true } : {}),
      ...(flaggedOnly ? { flaggedOnly: true } : {}),
    };
  }, [accountId, enabled, flaggedOnly, folderId, inboxes, queryKey, sort, unified, unreadOnly]);

  /** Sync now (debounced like the rest): after a stream gap, say. */
  const requestSync = useCallback(() => {
    if (target) scheduleConversationSync(client, api, target);
  }, [api, client, target]);

  useEffect(() => {
    if (!target) return;
    return subscribeConversationSignals(client, (signal) => {
      if (!target.lanes.some((lane) => signalReaches(signal, lane.accountId, lane.folderId))) return;
      scheduleConversationSync(client, api, target);
    });
  }, [api, client, target]);

  return { query, queryKey, rows, requestSync };
}
